import { useState, useRef, useEffect } from 'react';
import { APP } from './tokens';
import { toast } from '../../hooks/use-toast';
import type { Folder } from '../../types';
import { AppFolderIcon } from './AppFolderIcon';

interface FolderPickerButtonProps {
  folders: Folder[];
  currentFolderId?: string | null;
  onSelect: (folderId: string) => void;
  onCreateFolder?: (name: string) => Promise<string> | string;
  /**
   * Optional handler that toggles a folder's `kind` between `'general'` and
   * `'contracts'`. When set, each folder row gets a small "★ Contracts"
   * pill: clicking it promotes the folder into the contract velocity
   * workflow knowledge base. Hidden when omitted so existing call sites
   * keep their plain "Add to project" behaviour.
   */
  onToggleContractsKind?: (folderId: string, nextKind: 'general' | 'contracts') => void;
}

export default function FolderPickerButton({
  folders,
  currentFolderId,
  onSelect,
  onCreateFolder,
  onToggleContractsKind,
}: FolderPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [optimisticFolderId, setOptimisticFolderId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCreating(false);
      setNewFolderName('');
      setSaving(false);
    }
  }, [open]);

  useEffect(() => {
    setOptimisticFolderId(null);
  }, [currentFolderId]);

  const selectFolder = (folder: Folder) => {
    setOptimisticFolderId(folder.id);
    onSelect(folder.id);
    toast({ title: `Saving to "${folder.name}"` });
  };

  const createAndSelectFolder = async () => {
    if (!onCreateFolder || saving) return;
    const trimmed = newFolderName.trim();
    if (!trimmed) return;

    const existing = folders.find((folder) => folder.name.trim().toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      selectFolder(existing);
      setCreating(false);
      setNewFolderName('');
      return;
    }

    setSaving(true);
    try {
      const folderId = await onCreateFolder(trimmed);
      setOptimisticFolderId(folderId);
      onSelect(folderId);
      toast({ title: `Created "${trimmed}"`, description: 'Document saved to the new project.' });
      setCreating(false);
      setNewFolderName('');
    } catch {
      toast({ title: 'Could not create project', description: 'Try again in a moment.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn-hover"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 5,
          padding: '4px 8px',
          gap: 4,
          border: `1px solid ${APP.borderQuickAction}`,
          backgroundColor: currentFolderId ? APP.bgMenuHover : 'transparent',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <path d="M6 1.5v9M1.5 6h9" stroke={APP.iconDefault} strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: 11, color: APP.iconDefault, lineHeight: '14px' }}>
          Add to project
        </span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}>
          <path d="M2.5 4l2.5 2.5L7.5 4" stroke={APP.iconDefault} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 252,
            maxHeight: 320,
            backgroundColor: APP.bgContextMenu,
            border: `1px solid ${APP.borderContextMenu}`,
            borderRadius: 8,
            boxShadow: APP.shadowContextMenu,
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            padding: 6,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '4px 6px 8px',
              borderBottom: `1px solid ${APP.borderContextMenu}`,
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: APP.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: APP.fontFamily }}>
              Save to project
            </span>
            {onCreateFolder && !creating && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  border: `1px solid ${APP.borderQuickAction}`,
                  backgroundColor: APP.bgMenuHover,
                  color: APP.textPrimary,
                  borderRadius: 999,
                  padding: '3px 8px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 500,
                  fontFamily: APP.fontFamily,
                  lineHeight: '14px',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                New
              </button>
            )}
          </div>

          {creating && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: 6,
                marginBottom: 4,
                borderRadius: 7,
                backgroundColor: APP.bgToolSearch,
                border: `1px solid ${APP.borderQuickAction}`,
              }}
            >
              <input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void createAndSelectFolder();
                  if (e.key === 'Escape') {
                    setCreating(false);
                    setNewFolderName('');
                  }
                }}
                placeholder="Project name"
                disabled={saving}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: APP.textPrimary,
                  fontFamily: APP.fontFamily,
                  fontSize: 13,
                  lineHeight: '18px',
                }}
              />
              <button
                type="button"
                onClick={() => void createAndSelectFolder()}
                disabled={saving || !newFolderName.trim()}
                title="Create project"
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  border: 'none',
                  backgroundColor: newFolderName.trim() ? '#D97645' : APP.bgSubmitBtn,
                  color: '#FFFFFF',
                  cursor: newFolderName.trim() && !saving ? 'pointer' : 'default',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7.5l2.5 2.5L11 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          )}

          <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {folders.length === 0 ? (
              <div style={{ padding: '14px 12px', fontSize: 12, color: APP.textMuted, textAlign: 'center', fontFamily: APP.fontFamily, lineHeight: '17px' }}>
                Create a project to save this document.
              </div>
            ) : (
              folders.map((folder) => {
                const isSelected = (optimisticFolderId ?? currentFolderId) === folder.id;
                const isContracts = folder.kind === 'contracts';
                return (
                  <div
                    key={folder.id}
                    className="btn-hover"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 6px',
                      borderRadius: 6,
                      backgroundColor: isSelected ? '#D9764514' : 'transparent',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => selectFolder(folder)}
                      style={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderRadius: 4,
                        padding: '3px 4px',
                        fontFamily: APP.fontFamily,
                        fontSize: 13,
                        fontWeight: isSelected ? 500 : 400,
                        color: APP.textPrimary,
                        lineHeight: '16px',
                        textAlign: 'left',
                        minWidth: 0,
                      }}
                    >
                      <AppFolderIcon width={16} height={13} />
                      <span style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
                      {isContracts && !onToggleContractsKind && (
                        <span title="Contracts knowledge base" style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, backgroundColor: '#F3E9E1', color: '#8C4A1F', fontWeight: 600, lineHeight: '12px', flexShrink: 0 }}>
                          ★ Contracts
                        </span>
                      )}
                      {isSelected && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M3 7.5l3 3 5-6" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                    {onToggleContractsKind && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleContractsKind(folder.id, isContracts ? 'general' : 'contracts');
                          toast({
                            title: isContracts
                              ? `"${folder.name}" removed from Contracts`
                              : `"${folder.name}" added to Contracts knowledge base`,
                          });
                        }}
                        title={isContracts ? 'Unmark as Contracts project' : 'Mark as Contracts project'}
                        style={{
                          flexShrink: 0,
                          border: `1px solid ${isContracts ? '#8C4A1F' : APP.borderQuickAction}`,
                          backgroundColor: isContracts ? '#F3E9E1' : 'transparent',
                          color: isContracts ? '#8C4A1F' : APP.textMuted,
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: 999,
                          cursor: 'pointer',
                          fontFamily: APP.fontFamily,
                          lineHeight: '14px',
                        }}
                      >
                        {isContracts ? '★ Contracts' : '+ Contracts'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {onToggleContractsKind && (
            <div style={{ marginTop: 4, padding: '6px 10px 4px', borderTop: `1px solid ${APP.borderContextMenu}`, fontSize: 10, color: APP.textMuted, fontFamily: APP.fontFamily, lineHeight: '14px' }}>
              Projects marked Contracts appear in the contract velocity workflow.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
